/**
 * ClientDetail — full client workspace: header, rolled-up totals,
 * pricing history, and the client's projects.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import {
  ArrowLeft, Building2, Mail, Globe, User, Pencil, TrendingUp,
  Clock, FolderKanban, Plus,
} from 'lucide-react';
import { wsApi, wsBase } from './wsApi';
import { PROJECT_STATUS, fmtDate } from './wsConstants';
import { ClientModal } from './ClientsList';

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    wsApi.getClientDetail(id)
      .then(setData)
      .catch(() => toast.error('Could not load client'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading…</div>;
  if (!data) return <div style={{ padding: 40 }}>Client not found.</div>;

  const t = data.totals || {};
  const pricing = data.pricing || [];

  return (
    <div>
      <button onClick={() => navigate(`${wsBase()}/clients`)} style={backBtn}>
        <ArrowLeft size={15} /> Clients
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
        <div style={{ width: 52, height: 52, borderRadius: 12, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Building2 size={24} color="#6d28d9" />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          {/* Up to 200 chars, possibly unbroken — wrap anywhere so the full
              name stays inside the header. */}
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827', overflowWrap: 'anywhere' }}>{data.company_name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: data.status === 'active' ? '#d1fae5' : '#f3f4f6', color: data.status === 'active' ? '#065f46' : '#6b7280' }}>
              {data.status === 'active' ? 'Active' : 'Inactive'}
            </span>
            {data.industry && <span style={{ fontSize: 13, color: 'var(--em-fg5)' }}>{data.industry}</span>}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
            {data.contact_name && <Meta Icon={User} text={data.contact_name} />}
            {data.email && <Meta Icon={Mail} text={data.email} />}
            {data.timezone && <Meta Icon={Globe} text={data.timezone} />}
          </div>
        </div>
        <button onClick={() => setEditing(true)} style={ghostBtn}><Pencil size={14} /> Edit</button>
      </div>

      {/* Totals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 26 }}>
        <Stat label="Revenue" value={`₹${(t.revenue || 0).toLocaleString()}`} color="#10b981" />
        <Stat label="Cost" value={`₹${(t.cost || 0).toLocaleString()}`} color="#ef4444" />
        <Stat label="Profit" value={`₹${(t.profit || 0).toLocaleString()}`} color={t.profit >= 0 ? '#10b981' : '#ef4444'} Icon={TrendingUp} />
        <Stat label="Margin" value={`${t.margin || 0}%`} color="#8b5cf6" />
        <Stat label="Hours" value={`${t.total_hours || 0}h`} sub={`${t.billable_hours || 0}h billable`} color="#3b82f6" Icon={Clock} />
      </div>

      {/* Pricing history */}
      <Section title="Pricing">
        {pricing.length === 0 ? (
          <Empty text="No pricing model set. Edit the client to add one." />
        ) : (
          <table style={table}>
            <thead><tr>{['Type', 'Amount', 'Expected Hours', 'Currency', 'Added'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {pricing.map((pm, i) => (
                <tr key={pm.id}>
                  <td style={td}>
                    <span style={{ textTransform: 'capitalize' }}>{pm.pricing_type}</span>
                    {i === 0 && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: '#065f46', background: '#d1fae5', padding: '2px 7px', borderRadius: 10 }}>Current</span>}
                  </td>
                  <td style={td}>₹{Number(pm.amount).toLocaleString()}{pm.pricing_type === 'hourly' ? '/hr' : ''}</td>
                  <td style={td}>{pm.expected_hours || '—'}</td>
                  <td style={td}>{pm.currency}</td>
                  <td style={td}>{fmtDate(pm.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Projects */}
      <Section
        title={`Projects (${data.projects?.length || 0})`}
        // Opens the Projects page with Add project already open for this client.
        action={<button onClick={() => navigate(`${wsBase()}/projects?new=1&client_id=${encodeURIComponent(id)}`)} style={miniGhost}><Plus size={13} /> New project</button>}
      >
        {(data.projects || []).length === 0 ? (
          <Empty text="No projects for this client yet." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, padding: 14 }}>
            {data.projects.map((p) => {
              const ps = PROJECT_STATUS[p.status] || PROJECT_STATUS.planning;
              const progress = p.task_count > 0 ? Math.round((p.done_count / p.task_count) * 100) : 0;
              return (
                <div key={p.id} onClick={() => navigate(`${wsBase()}/projects/${p.id}`)}
                  style={{ border: '1px solid var(--em-border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: 'var(--em-bg-card)' }}>
                  <div style={{ height: 5, background: p.color || '#f54b01' }} />
                  <div style={{ padding: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <FolderKanban size={14} color="#9ca3af" />
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#111827', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: ps.bg, color: ps.text }}>{ps.label}</span>
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>{p.done_count}/{p.task_count} · {progress}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {editing && (
        <ClientModal initial={data} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
      )}
    </div>
  );
}

function Meta({ Icon, text }) {
  return <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--em-fg5)' }}><Icon size={13} /> {text}</span>;
}
function Stat({ label, value, sub, color, Icon }) {
  return (
    <div style={{ background: 'var(--em-bg-card)', border: '1px solid var(--em-border)', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--em-fg5)', marginBottom: 6 }}>
        {Icon && <Icon size={13} color={color} />} {label}
      </div>
      <div style={{ fontSize: 19, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function Section({ title, action, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>{title}</h2>
        {action}
      </div>
      <div style={{ background: 'var(--em-bg-card)', border: '1px solid var(--em-border)', borderRadius: 12, overflow: 'hidden' }}>{children}</div>
    </div>
  );
}
function Empty({ text }) {
  return <div style={{ padding: 28, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{text}</div>;
}

const backBtn = { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--em-fg5)', fontSize: 13, fontWeight: 600, marginBottom: 14, padding: 0 };
const ghostBtn = { display: 'flex', alignItems: 'center', gap: 7, background: 'var(--em-bg-card)', color: '#374151', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const miniGhost = { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--em-bg-card)', color: 'var(--em-fg5)', border: '1px solid var(--em-border)', borderRadius: 7, padding: '6px 11px', cursor: 'pointer', fontSize: 12, fontWeight: 600 };
const table = { width: '100%', borderCollapse: 'collapse' };
const th = { textAlign: 'left', fontSize: 11.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4, padding: '11px 16px', borderBottom: '1px solid #f3f4f6', background: '#fafafa' };
const td = { padding: '11px 16px', fontSize: 13.5, color: '#374151', borderBottom: '1px solid #f3f4f6' };
